import {
  ArrowsAltOutlined,
  ControlOutlined,
  DatabaseOutlined,
  DesktopOutlined,
  EuroCircleOutlined,
  FormOutlined,
  ProjectOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { SidebarItem } from "src/core/components/LeftSidebar/LeftSidebar";
import { useViewPortQuery } from "src/core/hooks/useViewPortQuery";
import { getSidebarItem } from "src/core/utils/sidebar";
import { AppFeatureEnum } from "src/enums";
import { AppPage } from "src/modules/AdminSidebar/AdminSidebar";
import { appFeaturesState } from "src/recoil/atoms";

export function useAdminSidebarItems(
  pageToAddChilds?: AppPage,
  childSidebarItems?: SidebarItem[]
) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isMobile = useViewPortQuery() === "mobile";
  const appFeatures = useRecoilValue(appFeaturesState);

  const sidebarItems = useMemo(() => {
    const getCurrentPageChildSidebarItems = (
      currenPage: AppPage
    ): SidebarItem[] | undefined => {
      if (pageToAddChilds && currenPage === pageToAddChilds) {
        if (childSidebarItems && childSidebarItems.length > 0) {
          return childSidebarItems;
        }
      }

      return undefined;
    };

    const getSidebarItemByFeatureEnum = (
      feature: AppFeatureEnum
    ): SidebarItem | undefined => {
      switch (feature) {
        case AppFeatureEnum.REPOSITORYFEATURE: {
          return getSidebarItem(
            t("sidebar.priceConfigurators"),
            "price-configurator/list",
            <DatabaseOutlined />,
            () => {
              navigate("/price-configurator/list");
            }
          );
        }
        case AppFeatureEnum.PRICECONFIGURATORFEATURE: {
          return getSidebarItem(
            t("sidebar.priceConfigurator"),
            "price-configurator",
            <DesktopOutlined />,
            () => {
              if (
                (getCurrentPageChildSidebarItems("price-configurator")
                  ?.length ?? 0) === 0
              ) {
                navigate("/price-configurator/manager/master-info/new");
              }
            },
            getCurrentPageChildSidebarItems("price-configurator")
          );
        }
        case AppFeatureEnum.COSTEXPLOSIONFEATURE: {
          return getSidebarItem(
            t("sidebar.costExplosion"),
            "cost-explosion",
            <EuroCircleOutlined />,
            () => {
              navigate("/cost-explosion");
            }
          );
        }
        case AppFeatureEnum.COSTCOMPARISONFEATURE: {
          return getSidebarItem(
            t("sidebar.costComparison"),
            "cost-comparison",
            <ArrowsAltOutlined />,
            () => {
              navigate("/cost-comparison");
            }
          );
        }

        case AppFeatureEnum.GLOBALMANUALADJUSTMENT: {
          return getSidebarItem(
            t("sidebar.globalManualAdjustments"),
            "global-manual-adjustments",
            <FormOutlined />,
            () => {
              navigate("/manual-adjustments");
            }
          );
        }
        case AppFeatureEnum.GLOBALCOSTREPORT: {
          return getSidebarItem(
            t("sidebar.globalCostReport"),
            "global-cost-report",
            <ProjectOutlined />,
            () => {
              navigate("/global-cost-report");
            }
          );
        }
        case AppFeatureEnum.ADMINISTRATIONFEATURE: {
          return getSidebarItem(
            t("sidebar.administration"),
            "administration",
            <ControlOutlined />,
            () => {
              navigate("/administration");
            }
          );
        }
        case AppFeatureEnum.DATAMANAGERFEATURE: {
          return getSidebarItem(
            t("sidebar.dataManaging"),
            "data-managing",
            <SettingOutlined />,
            () => {
              navigate("/data-managing");
            },
            undefined,
            undefined,
            undefined,
            { position: "absolute", bottom: isMobile ? 0 : 110 }
          );
        }
        default: {
          return undefined;
        }
      }
    };

    let result: SidebarItem[] = [
      getSidebarItem(t("sidebar.home"), "home", <UserOutlined />, () =>
        navigate("/")
      ),
    ];

    appFeatures.forEach((feature) => {
      const item = getSidebarItemByFeatureEnum(feature);

      if (item) {
        result.push(item);
      }
    });
    return result;
  }, [appFeatures, childSidebarItems, isMobile, navigate, pageToAddChilds, t]);

  return {
    sidebarItems,
  };
}
